import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useSurfacePreference } from "../../../../../../../hooks/use-surface-preference.ts"
import {
  type SurfaceKey,
  surfaceKey
} from "../../../../../../User/redux/ui-preferences.types.ts"
import { type GanttZoom, parseStoredZoom } from "./gantt-scale.ts"

const KEY_PREFIX = "project.gantt.zoom"

// A pinch sends dozens of steps a second; only where it settles is saved.
const SETTLE_MS = 600

export function ganttZoomKey(projectId?: string | null): SurfaceKey {
  const id = (projectId ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return surfaceKey(id ? `${KEY_PREFIX}.${id}` : KEY_PREFIX)
}

/**
 * The zoom this user last chose on this project's Gantt, or null when they
 * never chose one (the chart then picks its own default). Only remembered
 * when there is a project to remember it against.
 */
export function useGanttZoom(
  projectId?: string | null
): [GanttZoom | null, (zoom: GanttZoom) => void] {
  const key = useMemo(() => ganttZoomKey(projectId), [projectId])
  const [stored, store] = useSurfacePreference<unknown>(key, null)
  const [chosen, setChosen] = useState<GanttZoom | null>(null)
  const pending = useRef<GanttZoom | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => setChosen(null), [key])

  const flush = useCallback(() => {
    clearTimeout(timer.current)
    if (pending.current !== null) store(pending.current)
    pending.current = null
  }, [store])

  useEffect(() => flush, [flush])

  const setZoom = useCallback(
    (next: GanttZoom) => {
      const value =
        typeof next === "number" ? Math.round(next * 1000) / 1000 : next
      setChosen(value)
      if (!projectId) return
      pending.current = value
      clearTimeout(timer.current)
      timer.current = setTimeout(
        flush,
        typeof value === "number" ? SETTLE_MS : 0
      )
    },
    [projectId, flush]
  )

  return [chosen ?? parseStoredZoom(stored), setZoom]
}
